import { ServerFilterClauses } from '~commonInfra/utils/ServerFilterClauses';
import { ServerApiMapper } from '~commonInfra/utils/ServerModelHelper';
import { TurnOffDataAccelerationForViews, TurnOnDataAccelerationForViews } from '~src/dataAcceleration/serverApi/requests/DataAccelerationRequest';
import { IDetailFetcher } from '~src/httpClient/fetchers/IDetailFetcher';
import { IListFetcher } from '~src/httpClient/fetchers/IListFetcher';
import { WebClientDetailFetcher } from '~src/httpClient/fetchers/resourceFetchers/WebClientDetailFetcher';
import { WebClientListResourceFetcher } from '~src/httpClient/fetchers/resourceFetchers/WebClientListResourceFetcher';
import { FieldValuesQuery } from '~src/httpClient/resources/FieldValuesQuery';
import { IActionResource, IDetailResource, IFieldValuesResource, IListResource } from '~src/httpClient/resources/Resource';
import { ResourceQuery } from '~src/httpClient/resources/ResourceQuery';
import { GetFieldValuesRequest } from '~src/httpClient/serverApi/requests/GetFieldValuesRequest';
import { IFieldValue } from '~src/httpClient/serverApi/types/IFieldValue';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { RequestHelper } from '~src/httpClient/utils/RequestHelper';
import { ResultHelper } from '~src/httpClient/utils/ResultHelper';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import { WebHttpClientWithErrorToastInstance } from '~src/httpClient/WebHttpClientWithErrorToast';
import { MarkRecentlyViewedRequest, RecentlyViewedObjectType } from '~src/recents/requests/MarkRecentlyViewedRequest';
import { ITaggableContent } from '~src/tags/ITaggableContent';
import { AddTagsToViewsRequest } from '~src/tags/serverApi/requests/AddTagsToViewsRequest';
import { RemoveTagsFromViewsRequest } from '~src/tags/serverApi/requests/RemoveTagsFromViewsRequest';
import { VizService } from '~src/viz/services/VizService';
import { IWorkbookBase, IWorkbookParentInfo } from '~src/workbook/models/IWorkbookInfo';

import { ViewMapper } from '../mappers/ViewMapper';
import { IViewBase, IViewDetailInfo, IViewInfo } from '../models/IViewInfo';
import { GetViewActionsRequest } from '../ServerApi/requests/GetViewActionsRequest';
import { GetViewByPathRequest, IGetViewByPathResult } from '../ServerApi/requests/GetViewByPathRequest';
import { GetViewsRequest } from '../ServerApi/requests/GetViewsRequest';
import { HideViewsRequest } from '../ServerApi/requests/HideViewsRequest';
import { RenameViewRequest } from '../ServerApi/requests/RenameViewRequest';
import { IViewActions } from '../ServerApi/types/IViewActions';
class ViewsResource implements IListResource<IViewInfo>, IFieldValuesResource, IDetailResource<IViewDetailInfo>, IActionResource<IViewActions>, ITaggableContent {

  private listFetcher: IListFetcher<IViewInfo> = new WebClientListResourceFetcher(GetViewsRequest, ServerApiMapper.views);
  private detailFetcher: IDetailFetcher<IViewDetailInfo> = new WebClientDetailFetcher('getView', ViewMapper.mapDetail);
  private fieldValuesFetcher: IListFetcher<IFieldValue> = new WebClientListResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);

  public isViewDetailInfo(item: any): item is IViewDetailInfo {
    return item.contentType &&
      item.contentType === 'view' &&
      (<IViewDetailInfo>item).hasOwnProperty('sheetType');
  }

  public isViewBase(item: any): item is IViewBase {
    return item.contentType &&
      item.contentType === 'view' &&
      item.hasOwnProperty('workbook');
  }

  public all(): ResourceQuery<IViewInfo> {
    return new ResourceQuery(this.listFetcher);
  }

  public getById(id: string): Promise<IViewDetailInfo> {
    return this.detailFetcher.fetchById(id);
  }

  public getByPath(path: string): Promise<IViewDetailInfo> {
    // TFS 1313385: location is partially encoded by the httpd apache backend,
    // which messes up fetching the embedded viz url if we don't remove the encodings.
    // Using decodeURI vs decodeURIComponent to preserve intential ampersand encodings better
    const decodedPath = decodeURI(path);
    const pathWithoutExtension = decodedPath.replace(VizService.VizExportFileTypeRegex, '');

    return WebHttpClient.sendRequest(new GetViewByPathRequest(pathWithoutExtension))
      .then((result: IGetViewByPathResult) => {
        const error = result?.errors?.[0].code;
        if (error) {
          return Promise.reject(error);
        } else {
          return Promise.resolve(ViewMapper.mapDetail(result));
        }
      });
  }

  public forWorkbook(workbook: IWorkbookBase | IWorkbookParentInfo): ResourceQuery<IViewInfo> {
    return this.all().withFilters(ServerFilterClauses.create({ workbook: workbook.id }));
  }

  public valuesForField(field: string): FieldValuesQuery {
    return new FieldValuesQuery(this.fieldValuesFetcher, field, 'view');
  }

  // Actions

  public actions(id: string): Promise<IViewActions> {
    return WebHttpClient.sendRequest(new GetViewActionsRequest(id));
  }

  public rename(id: string, name: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(new RenameViewRequest(id, name));
  }

  public hide(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new HideViewsRequest(idsBlock)),
      ResultHelper.combineResults);
  }

  public addTags(ids: string[], tags: string[]): Promise<IResult> {
    return (tags.length > 0)
      ? WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
        RequestHelper.buildRequests2(ids, tags,
          (idsBlock, tagsBlock) => new AddTagsToViewsRequest(idsBlock, tagsBlock)),
        ResultHelper.combineResults)
      : Promise.resolve({});
  }

  public removeTags(ids: string[], tags: string[]): Promise<IResult> {
    return (tags.length > 0)
      ? WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
        RequestHelper.buildRequests2(ids, tags,
          (idsBlock, tagsBlock) => new RemoveTagsFromViewsRequest(idsBlock, tagsBlock)),
        ResultHelper.combineResults)
      : Promise.resolve({});
  }

  public changeTags(ids: string[], tagsToAdd: string[], tagsToRemove: string[]): Promise<IResult> {
    return this.removeTags(ids, tagsToRemove)
      .then((result: IResult) => (ResultHelper.hasErrors(result) ? result : this.addTags(ids, tagsToAdd)));
  }

  public markRecentlyViewed(id: string) {
    return WebHttpClient.sendRequest(new MarkRecentlyViewedRequest({
      objectId: id,
      objectType: RecentlyViewedObjectType.View
    }));
  }

  public turnOnDataAccelerationForViews(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequests(
      RequestHelper.buildRequests(ids,
        (idsBlock) => new TurnOnDataAccelerationForViews({
          targetIds: idsBlock,
        })),
      ResultHelper.combineResults);
  }

  public turnOffDataAccelerationForViews(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequests(
      RequestHelper.buildRequests(ids,
        (idsBlock) => new TurnOffDataAccelerationForViews({
          targetIds: idsBlock,
        })),
      ResultHelper.combineResults);
  }

}

export const Views = new ViewsResource();
